import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, useToast } from "ui";
import { z } from "zod";

import { dateSearchParamSchema, getDateSearchParam } from "src/calendar/utils";
import { BillingProductFeatureGate } from "src/components/BillingProductFeatureGate";
import { PrimaryNavigationTabs } from "src/components/NavigationTabs";
import { programGridZoomSearchSchema } from "src/contexts/ProgramGridZoomContext";
import { WorkflowStoreProvider } from "src/contexts/WorkflowStore";
import { useChildRouteSegment } from "src/hooks/useChildRouteSegment";
import { TabLink } from "src/layout/TabLink";
import {
  getCalendarDefaultWeekIndex,
  getCalendarStartWeekIndex,
} from "src/lib/calendarViewRoute";
import { loaderHandler } from "src/lib/loaderHandler";
import { fetchWorkflowInitialData } from "src/queries/workflows";
import { WorkflowProgramProviders } from "src/workflows/WorkflowProgramProviders";

const routeSearchSchema = z.object({
  date: dateSearchParamSchema,
  selectedWorkout: z.string().optional(),
  workoutItemId: z.string().optional(),
  workflowComments: z.string().optional(),
  workflowCommentsAuthor: z.string().optional(),
  zoom: programGridZoomSearchSchema,
});

export const Route = createFileRoute("/_auth/_coach/workflows/$workflowId")({
  component: RouteComponent,
  validateSearch: routeSearchSchema,
  ssr: true,
  shouldReload: false,
  async loader({ params, location }) {
    return await loaderHandler(async () => {
      const initialData = await fetchWorkflowInitialData(
        params.workflowId,
        getCalendarStartWeekIndex(
          getCalendarDefaultWeekIndex("month", "measured"),
          getDateSearchParam(location.href),
        ),
      );

      return {
        data: initialData,
        breadcrumb: {
          id: initialData.workflow.id,
          label: initialData.workflow.name,
          href: `/workflows/${initialData.workflow.id}`,
        },
      };
    });
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const params = Route.useParams();
  const search = Route.useSearch();
  const router = useRouter();
  const { data } = Route.useLoaderData();
  const toast = useToast();
  const queryClient = useQueryClient();

  const tabId = useChildRouteSegment() ?? "program";

  const searchParams = search.date ? { date: search.date } : {};

  const programUrl = router.buildLocation({
    to: "/workflows/$workflowId",
    params: {
      workflowId: params.workflowId,
    },
    search: searchParams,
  });

  const scheduleUrl = router.buildLocation({
    to: "/workflows/$workflowId/schedule",
    params: {
      workflowId: params.workflowId,
    },
    search: searchParams,
  });

  const athletesUrl = router.buildLocation({
    to: "/workflows/$workflowId/athletes",
    params: {
      workflowId: params.workflowId,
    },
    search: searchParams,
  });

  const settingsUrl = router.buildLocation({
    to: "/workflows/$workflowId/settings",
    params: {
      workflowId: params.workflowId,
    },
    search: searchParams,
  });

  const navigationContextValue = useMemo(() => {
    return {
      tabs: [
        {
          tabId: "program",
          label: t("workflow.tabs.program"),
          href: programUrl.href,
        },
        {
          tabId: "schedule",
          label: t("workflow.tabs.schedule"),
          href: scheduleUrl.href,
        },
        {
          tabId: "athletes",
          label: t("workflow.tabs.athletes"),
          href: athletesUrl.href,
        },
        {
          tabId: "settings",
          label: t("workflow.tabs.settings"),
          href: settingsUrl.href,
        },
      ],
      components: {
        Link: TabLink,
      },
      getCurrentTabId: () => {
        return tabId;
      },
    };
  }, [tabId, t, programUrl, scheduleUrl, athletesUrl, settingsUrl]);

  function handleLoadError(error: unknown) {
    toast.toast({
      title: t("calendar.loadFailed"),
      description: error instanceof Error ? error.message : undefined,
      variant: "destructive",
    });
  }

  return (
    <BillingProductFeatureGate feature="trainingGroups">
      <WorkflowStoreProvider
        key={data.workflow.id}
        workflow={data.workflow}
        initialWeeks={data.weeks}
        locale={i18n.language}
        queryClient={queryClient}
        onLoadError={handleLoadError}
      >
        <PrimaryNavigationTabs.Context.Provider value={navigationContextValue}>
          <PrimaryNavigationTabs.TabsRoot className="w-full h-full flex flex-col">
            <div className="items-center top-0 flex border-b sticky bg-white h-sztama-nav-tabs-height z-10 justify-between pr-4">
              <PrimaryNavigationTabs.TabsList className="capitalize" />
              <Badge variant="outline">
                {data.workflow.type === "DATED"
                  ? t("workflow.type.dated")
                  : t("workflow.type.sequential")}
              </Badge>
            </div>
            <PrimaryNavigationTabs.TabsContent className="w-full m-0 flex-1 min-h-0 flex flex-col">
              <WorkflowProgramProviders>
                <Outlet />
              </WorkflowProgramProviders>
            </PrimaryNavigationTabs.TabsContent>
          </PrimaryNavigationTabs.TabsRoot>
        </PrimaryNavigationTabs.Context.Provider>
      </WorkflowStoreProvider>
    </BillingProductFeatureGate>
  );
}
